import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { TrendingUp, ChevronRight, X, Mail } from "lucide-react";
import api from "@/lib/api";

const OLD_PLANS = [
  { key: "starter",         name: "Starter",         price: 397 },
  { key: "front_office",    name: "Front Office",    price: 697 },
  { key: "multi_location",  name: "Multi-Location",  price: 997 },
];

const AUTO_ENROLL_THRESHOLD = 10000; // matches ROI_AUTO_ENROLL_THRESHOLD default

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

/**
 * Shared, self-contained ROI calculator. Accepts optional per-industry
 * defaults so the same component drives both the generic /pricing calculator
 * and the industry-specific /for/:industry calculators.
 *
 * Sends debounced intent signals to /public/roi-signal for high-intent
 * lead alerts (identical behavior to the original Pricing.jsx implementation).
 */
export default function ROICalculator({
  defaults,
  industryKey = "",
  industryLabel = "",
  showEyebrow = true,
  compact = false,
}) {
  const d = defaults || {};
  const [weeklyCalls, setWeeklyCalls] = useState(d.weekly_calls ?? 40);
  const [avgValue, setAvgValue] = useState(d.avg_value ?? 220);
  const [missPct, setMissPct] = useState(d.miss_pct ?? 25);

  // Re-hydrate when defaults change (industry switch)
  useEffect(() => {
    if (defaults) {
      setWeeklyCalls(defaults.weekly_calls);
      setAvgValue(defaults.avg_value);
      setMissPct(defaults.miss_pct);
      interactedRef.current = false;
    }
  }, [defaults]);

  const roi = useMemo(() => {
    const callsPerMonth = Math.round(weeklyCalls * 4.33);
    const missed = Math.round(callsPerMonth * (missPct / 100));
    const jobsLost = Math.round(missed * 0.5);
    const revenueLost = jobsLost * avgValue;
    let suggested = OLD_PLANS[0];
    if (callsPerMonth > 400) suggested = OLD_PLANS[2];
    else if (callsPerMonth > 150) suggested = OLD_PLANS[1];
    const net = revenueLost - suggested.price;
    return { callsPerMonth, missed, jobsLost, revenueLost, suggested, net };
  }, [weeklyCalls, avgValue, missPct]);

  // Intent-signal plumbing (dedup'd from Pricing.jsx)
  const sessionIdRef = useRef(null);
  const interactedRef = useRef(false);
  const signalTimerRef = useRef(null);
  const modalDismissedRef = useRef(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [captureEmail, setCaptureEmail] = useState("");
  const [captureSubmitting, setCaptureSubmitting] = useState(false);
  const [captureDone, setCaptureDone] = useState(false);

  if (!sessionIdRef.current && typeof window !== "undefined") {
    let sid = window.localStorage.getItem("co_roi_sid");
    if (!sid) {
      sid = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`);
      window.localStorage.setItem("co_roi_sid", sid);
    }
    sessionIdRef.current = sid;
    // Persist "already captured" flag across reloads so we don't nag returning visitors
    if (window.localStorage.getItem("co_roi_captured") === "1") {
      modalDismissedRef.current = true;
    }
  }

  useEffect(() => {
    if (!interactedRef.current) return;
    if (signalTimerRef.current) clearTimeout(signalTimerRef.current);
    signalTimerRef.current = setTimeout(() => {
      const convId = typeof window !== "undefined"
        ? window.localStorage.getItem("co_widget_conv") || ""
        : "";
      api.post("/public/roi-signal", {
        session_id: sessionIdRef.current,
        weekly_calls: Math.round(weeklyCalls),
        avg_value: Math.round(avgValue),
        miss_pct: Math.round(missPct),
        revenue_lost: Math.round(roi.revenueLost),
        net_upside: Math.round(roi.net),
        suggested_plan_key: roi.suggested.key,
        suggested_plan_name: roi.suggested.name,
        suggested_plan_price: roi.suggested.price,
        conversation_id: convId,
      }).catch(() => {});

      // Trigger email capture modal once net upside crosses the auto-enroll
      // threshold. We only show it once per session; if the user already
      // provided an email or dismissed it, we stay out of the way.
      if (
        roi.net >= AUTO_ENROLL_THRESHOLD
        && !modalDismissedRef.current
        && !captureDone
        && !showCaptureModal
      ) {
        setShowCaptureModal(true);
      }
    }, 900);
    return () => { if (signalTimerRef.current) clearTimeout(signalTimerRef.current); };
  }, [weeklyCalls, avgValue, missPct, roi.revenueLost, roi.net, roi.suggested]);
  const markInteracted = () => { interactedRef.current = true; };

  const submitCapture = async () => {
    const email = captureEmail.trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) return;
    setCaptureSubmitting(true);
    try {
      const convId = typeof window !== "undefined"
        ? window.localStorage.getItem("co_widget_conv") || ""
        : "";
      await api.post("/public/roi-signal", {
        session_id: sessionIdRef.current,
        weekly_calls: Math.round(weeklyCalls),
        avg_value: Math.round(avgValue),
        miss_pct: Math.round(missPct),
        revenue_lost: Math.round(roi.revenueLost),
        net_upside: Math.round(roi.net),
        suggested_plan_key: roi.suggested.key,
        suggested_plan_name: roi.suggested.name,
        suggested_plan_price: roi.suggested.price,
        email,
        conversation_id: convId,
      });
      if (typeof window !== "undefined") {
        window.localStorage.setItem("co_roi_captured", "1");
      }
      setCaptureDone(true);
      modalDismissedRef.current = true;
    } catch (e) {
      // Silent: they can still hit the CTA button
    } finally {
      setCaptureSubmitting(false);
    }
  };

  const dismissCapture = () => {
    modalDismissedRef.current = true;
    setShowCaptureModal(false);
  };

  const industryHint = industryLabel
    ? `Pre-loaded with typical ${industryLabel.toLowerCase()} numbers — adjust to your reality.`
    : "Drag the sliders to match your business — the numbers update instantly.";

  return (
    <div id="calc">
      {showEyebrow && (
        <>
          <div className="co-eyebrow-dark">
            {industryLabel ? `${industryLabel} ROI` : "Calculate your fit"}
          </div>
          <h2 className="co-h2 co-h2-light">
            {industryLabel
              ? `See what missed ${industryLabel.toLowerCase()} calls are actually costing you.`
              : "See what missed calls are actually costing you."}
          </h2>
          <p className="co-lead-dark" style={{ maxWidth: 640 }}>{industryHint}</p>
        </>
      )}

      <div className="co-calc-grid" style={compact ? { marginTop: 24 } : undefined}>
        <div className="co-calc-inputs" data-testid={industryKey ? `pricing-calculator-${industryKey}` : "pricing-calculator"}>
          <SliderInput
            label="Calls you get per week"
            value={weeklyCalls} setValue={(v) => { markInteracted(); setWeeklyCalls(v); }}
            min={5} max={300} step={1}
            testId="calc-weekly-calls"
          />
          <SliderInput
            label="Average job / appointment value"
            value={avgValue} setValue={(v) => { markInteracted(); setAvgValue(v); }}
            min={50} max={5000} step={10} prefix="$"
            testId="calc-avg-value"
          />
          <SliderInput
            label="% of calls you currently miss"
            value={missPct} setValue={(v) => { markInteracted(); setMissPct(v); }}
            min={5} max={70} step={1} suffix="%"
            testId="calc-miss-pct"
          />
        </div>

        <div className="co-calc-results">
          <div className="co-calc-hero">
            <div className="co-calc-hero-label">Estimated revenue lost per month</div>
            <div className="co-calc-hero-num" data-testid="calc-revenue-lost">{fmt(roi.revenueLost)}</div>
            <div className="co-calc-hero-sub">From calls that go unanswered and never call back</div>
          </div>
          <div className="co-calc-rows">
            <Row label="Missed calls per month" value={roi.missed} testId="calc-missed" />
            <Row label="Estimated jobs lost" value={roi.jobsLost} testId="calc-jobs-lost" />
            <Row label="Clients OnRamp monthly cost" value={fmt(roi.suggested.price)} testId="calc-plan-cost" />
            <Row
              label="Net monthly upside"
              value={<span className="co-calc-net">+{fmt(roi.net)}/mo</span>}
              testId="calc-net-upside"
              strong
            />
          </div>
          <div className="co-calc-plan-suggest" data-testid="calc-suggested-plan">
            <TrendingUp className="w-4 h-4" />
            Suggested plan: <strong>{roi.suggested.name}</strong> (${roi.suggested.price}/mo)
          </div>
          <Link to="/#contact" className="co-btn-yellow" style={{ marginTop: 24 }}>
            Book my demo <ChevronRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {showCaptureModal && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center p-4"
          style={{ background: "rgba(10,10,10,0.72)" }}
          onClick={dismissCapture}
          data-testid="roi-capture-modal"
        >
          <div
            className="bg-white w-full max-w-md border border-neutral-200 shadow-2xl"
            style={{ animation: "co-fade-in 200ms ease-out" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b border-neutral-100 flex items-start justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-widest font-mono text-amber-700">
                  You&apos;re leaving <strong>{fmt(roi.net)}/mo</strong> on the table
                </div>
                <div className="text-lg font-bold text-neutral-900 mt-1">
                  Want us to build your free preview?
                </div>
              </div>
              <button
                onClick={dismissCapture}
                className="text-neutral-400 hover:text-neutral-900"
                data-testid="roi-capture-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {!captureDone ? (
              <>
                <div className="px-6 py-5 space-y-3">
                  <p className="text-sm text-neutral-700 leading-relaxed">
                    Drop your work email and we&apos;ll send you a live AI receptionist demo
                    built for your business — usually inside 48 hours, no card required.
                  </p>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <input
                      type="email"
                      autoFocus
                      placeholder="you@yourbusiness.com"
                      value={captureEmail}
                      onChange={(e) => setCaptureEmail(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") submitCapture();
                      }}
                      className="w-full border border-neutral-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-neutral-900"
                      data-testid="roi-capture-email-input"
                    />
                  </div>
                  <p className="text-xs text-neutral-500">
                    We&apos;ll only use this to send your preview and follow up if you&apos;re
                    interested — never sold, never spammed.
                  </p>
                </div>
                <div className="px-6 py-4 border-t border-neutral-100 flex items-center justify-end gap-2">
                  <button
                    onClick={dismissCapture}
                    className="text-sm text-neutral-500 hover:text-neutral-900 px-3 py-2"
                    data-testid="roi-capture-skip"
                  >
                    No thanks
                  </button>
                  <button
                    onClick={submitCapture}
                    disabled={captureSubmitting || !captureEmail}
                    className="co-btn-yellow"
                    style={{ padding: "10px 18px", fontSize: 14 }}
                    data-testid="roi-capture-submit"
                  >
                    {captureSubmitting ? "Sending…" : "Send my free preview"}
                  </button>
                </div>
              </>
            ) : (
              <div className="px-6 py-8 text-center">
                <div className="text-4xl mb-3">✓</div>
                <div className="text-lg font-bold text-neutral-900">You&apos;re on the list.</div>
                <p className="text-sm text-neutral-600 mt-2">
                  We just kicked off your preview. Watch your inbox — first note lands
                  in a few minutes.
                </p>
                <button
                  onClick={() => setShowCaptureModal(false)}
                  className="mt-5 text-sm font-mono uppercase tracking-widest text-neutral-500 hover:text-neutral-900"
                  data-testid="roi-capture-dismiss-success"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SliderInput({ label, value, setValue, min, max, step, prefix = "", suffix = "", testId }) {
  return (
    <div className="co-slider">
      <div className="co-slider-head">
        <label className="co-slider-label">{label}</label>
        <div className="co-slider-value">{prefix}{value.toLocaleString()}{suffix}</div>
      </div>
      <input
        type="range"
        className="co-range"
        min={min} max={max} step={step} value={value}
        onChange={(e) => setValue(Number(e.target.value))}
        data-testid={testId}
      />
      <div className="co-slider-scale">
        <span>{prefix}{min.toLocaleString()}{suffix}</span>
        <span>{prefix}{max.toLocaleString()}{suffix}</span>
      </div>
    </div>
  );
}

function Row({ label, value, strong, testId }) {
  return (
    <div className={`co-calc-row ${strong ? "co-calc-row-strong" : ""}`} data-testid={testId}>
      <span>{label}</span>
      <span className="co-calc-row-val">{value}</span>
    </div>
  );
}
